// Built-in kernels — the curated, read-only base set shipped with the
// dashboard. Same role as the built-in SIGNALS set (triggers/exampleTriggers.js):
// hard-coded definitions that ship NATIVELY in code, never persisted to
// localStorage, but browsable / mountable / forkable everywhere a user kernel is.
// Forking one (kernelStore.forkKernel) writes a mutable user-owned copy; the
// originals here are immutable.
//
// A kernel is the same deterministic recipe documented in kernelStore.js:
//   { id, command, title, description, scopeMode, params, steps[], render, renderSource }
// plus a `builtin: true` marker the stores + UI key off (read-only badge, no
// delete/save, fork-to-customize).
//
// Content is guided by the dashboard's sample starter prompts (ChatConsole
// SAMPLE_QUERIES) — "what your org uses AI for", "MCP tool sprawl", "skills in
// the field", "where token capital went", "new since last week", "how
// concentrated is AI usage".
//
// NO anomaly/typicality kernel: the analytics graph carries no typicality
// verdict. Non-prompt frames are marked on `analysis_mode` (NON_PROMPT), a
// row-stage tag, not a verdict. Durable detection output in `signal_tags` is
// server-queryable and may power a built-in. Browser-local norms and intents
// are not server-queryable, so a built-in must not fabricate them.
//
// SCOPE-AWARENESS. Every kernel that CAN narrow to the active drilldown is
// authored prompt-rooted (ENTITY_TYPE_PROMPT, field refs on alias "p") and
// flagged scopeMode:"scoped" — kernelRunner calls applyScopeToSpec(spec, scope)
// at run time so the same kernel re-parameterises to an endpoint / cluster /
// provider / model / … scope, or runs tenant-wide at the root ([] scope). Token
// accounting can only come off ENTITY_TYPE_TOKEN, which the scope injector can't
// reconstruct from a PROMPT root, so the token kernels are scopeMode:"global"
// (always tenant-wide; the runner surfaces a scope warning if opened scoped) —
// the Token-spend canvas calls this out in its captions.

import { promptCount } from "../promptMetrics.js";
import { BUILTIN_KERNEL_IDS } from "./bundledArtifactIds.js";
import { DETECTION_COMMAND_OVERRIDES } from "../signalIdentity.js";

// Stable, fixed timestamps — built-ins don't change per-tenant, so they carry a
// constant identity. Date.now() is intentionally NOT used (these are shipped
// constants, not authored-at-runtime objects). A descending nudge keeps the
// shipped ORDER stable when a store sorts by updatedAt.
const BUILTIN_TS = Date.UTC(2026, 0, 1);
const DETECTION_COMMAND_OVERRIDES_PY = JSON.stringify(DETECTION_COMMAND_OVERRIDES);

// ── spec fragment helpers (run at module load; produce plain literals) ───────
const PROMPT_ROOT = { alias: "p", entity: "ENTITY_TYPE_PROMPT" };
const p = (field) => ({ stepAlias: "p", field });
const tk = (field) => ({ stepAlias: "tk", field });
const inv = (field) => ({ stepAlias: "inv", field });
const ec = (field) => ({ stepAlias: "ec", field });
const cluster = (field) => ({ stepAlias: "c", field });
const st = (field) => ({ stepAlias: "st", field });
const days = (n) => ({ relative: { durationSeconds: String(n * 86400) } });
const REAL_ENDPOINT_FILTER = [{ pred: {
    field: { stepAlias: "e", field: "DIMENSION_FIELD_ENDPOINT_IS_SYNTHETIC" },
    op: "OPERATOR_EQ",
    value: false,
} }];
// User-facing activity counts reconcile matching Browser and Sync captures.
const countPrompts = (outputAlias = "value") => promptCount(outputAlias);
const distinct = (outputAlias, field) => ({
    outputAlias,
    computed: { field: p(field), op: "AGGREGATE_OP_COUNT_DISTINCT" },
});
const desc = (outputAlias) => [{ outputAlias, direction: "DIRECTION_DESC" }];
// Anthropic-predicated cache sums (cache columns are only additive/meaningful
// where the provider keeps them disjoint from input — see tokenVolume.js).
const cacheReadMetric = {
    outputAlias: "cache_read_tokens",
    standard: {
        stepAlias: "tk",
        metric: "METRIC_TYPE_TOKEN_SUM_CACHED_INPUT_TOKENS",
        predicate: { pred: { field: tk("DIMENSION_FIELD_TOKEN_PROVIDER"), op: "OPERATOR_EQ", value: "anthropic" } },
    },
};
const inputMetric = { outputAlias: "input_tokens", standard: { stepAlias: "tk", metric: "METRIC_TYPE_TOKEN_SUM_INPUT_TOKENS" } };
const outputMetric = { outputAlias: "output_tokens", standard: { stepAlias: "tk", metric: "METRIC_TYPE_TOKEN_SUM_OUTPUT_TOKENS" } };
const reasoningMetric = { outputAlias: "reasoning_tokens", standard: { stepAlias: "tk", metric: "METRIC_TYPE_TOKEN_SUM_REASONING_TOKENS" } };

// Pull a single-row analytics result down to one object so stat/kpis (which
// expect an object, not a 1-element array) read fields directly.
const PICK_FIRST_ROW = `result = (inputs.get("rows") or [{}])[0]\n`;

// Kernel constructor — stamps the shared shape + builtin marker.
const k = ({ id, command, title, description, scopeMode = "scoped", steps, render, renderSource, params = {} }) => ({
    id,
    command,
    title,
    description,
    scopeMode,
    builtin: true,
    params,
    steps,
    render,
    renderSource: renderSource || steps[steps.length - 1].id,
    createdAt: BUILTIN_TS,
    updatedAt: BUILTIN_TS,
});

// ─────────────────────────────────────────────────────────────────────────
// A. ACTIVITY & COMPOSITION — "what your org uses AI for" (scoped)
// ─────────────────────────────────────────────────────────────────────────

const activitySummary = k({
    id: BUILTIN_KERNEL_IDS["activity-summary"],
    command: "activity-summary",
    title: "Activity summary",
    description: "Headline counts for the current scope over the last 30 days: total prompts and the distinct endpoints, sessions, and models behind them. Re-runs narrowed to whatever you've drilled into.",
    steps: [
        {
            kind: "analytics_query", id: "q", spec: {
                root: PROMPT_ROOT,
                timeRange: days(30),
                output: {
                    metrics: [
                        countPrompts("prompts"),
                        distinct("endpoints", "DIMENSION_FIELD_PROMPT_ENDPOINT_ID"),
                        distinct("sessions", "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID"),
                        distinct("models", "DIMENSION_FIELD_PROMPT_MODEL"),
                    ],
                },
            },
        },
        { kind: "python", id: "pick", inputs: { rows: "q" }, code: PICK_FIRST_ROW },
    ],
    renderSource: "pick",
    render: {
        kind: "kpis",
        tiles: [
            { label: "Prompts", valueField: "prompts", format: "integer" },
            { label: "Endpoints", valueField: "endpoints", format: "integer" },
            { label: "Sessions", valueField: "sessions", format: "integer" },
            { label: "Models", valueField: "models", format: "integer" },
        ],
    },
});

const promptVolume = k({
    id: BUILTIN_KERNEL_IDS["prompt-volume"],
    command: "prompt-volume",
    title: "Prompt volume (daily)",
    description: "Daily prompts in the current scope over the last 30 days.",
    steps: [
        {
            kind: "analytics_query", id: "q", spec: {
                root: PROMPT_ROOT,
                timeRange: days(30),
                output: {
                    dimensions: [{ field: p("DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP"), outputAlias: "bucket", bucketSeconds: 86400, fillBuckets: true }],
                    metrics: [countPrompts("value")],
                },
                orderBy: [{ outputAlias: "bucket", direction: "DIRECTION_ASC" }],
            },
        },
    ],
    render: { kind: "chart", mode: "line", valueField: "value", range: "1M", title: "Prompts per day", empty: "No activity in range" },
});

const agentSurfaces = k({
    id: BUILTIN_KERNEL_IDS["agent-surfaces"],
    command: "agent-surfaces",
    title: "Agent surfaces (harnesses)",
    description: "Prompt volume by agent surface in the current scope over the last 30 days. Unspecified is unclassified trace attribution, not a named AI Agent harness.",
    steps: [
        {
            kind: "analytics_query", id: "q", spec: {
                root: PROMPT_ROOT,
                timeRange: days(30),
                output: {
                    dimensions: [
                        { field: p("DIMENSION_FIELD_PROMPT_AGENT_TYPE"), outputAlias: "agent_type" },
                        { field: p("DIMENSION_FIELD_PROMPT_PROVIDER"), outputAlias: "provider" },
                    ],
                    metrics: [countPrompts("value")],
                },
                orderBy: desc("value"),
                limit: 40,
            },
        },
        {
            kind: "python",
            id: "labels",
            inputs: { rows: "q" },
            // Maps each provider tag to the legacy agent_type spelling for the
            // same provider, so a provider-labelled row merges into that
            // surface's existing bar rather than opening a second one. Mirrors
            // AiProvider::to_legacy_agent_type composed with AGENT_TYPE_NAMES.
            code: `provider_labels = {
    "anthropic": "Claude",
    "openai": "Chatgpt",
    "google": "Gemini",
    "microsoft": "Copilot",
    "cursor": "Cursor",
    "deepseek": "Deepseek",
    "groq": "Groq",
    "mistral": "Mistral",
    "openrouter": "Openrouter",
    "perplexity": "Perplexity",
    "together-ai": "TogetherAi",
    "huggingface": "HuggingFace",
    "xai": "Xai",
}
totals = {}
for row in inputs.get("rows") or []:
    agent_type = str(row.get("agent_type") or "").strip()
    provider = str(row.get("provider") or "").strip()
    if agent_type and agent_type != "Unspecified":
        label = agent_type
    elif provider:
        label = provider_labels.get(
            provider.lower(),
            provider.replace("_", " ").replace("-", " ").title(),
        )
    else:
        label = "Unspecified"
    try:
        value = int(float(row.get("value") or 0))
    except (TypeError, ValueError):
        value = 0
    totals[label] = totals.get(label, 0) + value
result = [{"label": label, "value": value} for label, value in totals.items()]
result.sort(key=lambda item: (-item["value"], item["label"]))
`,
        },
    ],
    renderSource: "labels",
    render: { kind: "bars", labelField: "label", valueField: "value", format: "integer", maxItems: 12, empty: "No agent activity in range" },
});

const providerMix = k({
    id: BUILTIN_KERNEL_IDS["provider-mix"],
    command: "provider-mix",
    title: "Provider mix",
    description: "Share of prompts by model provider in the current scope over the last 30 days.",
    steps: [
        {
            kind: "analytics_query", id: "q", spec: {
                root: PROMPT_ROOT,
                timeRange: days(30),
                output: {
                    dimensions: [{ field: p("DIMENSION_FIELD_PROMPT_PROVIDER"), outputAlias: "label" }],
                    metrics: [countPrompts("value")],
                },
                orderBy: desc("value"),
                limit: 15,
            },
        },
    ],
    render: { kind: "treemap", labelField: "label", valueField: "value", format: "integer", maxItems: 15, empty: "No provider activity in range" },
});

const modelMix = k({
    id: BUILTIN_KERNEL_IDS["model-mix"],
    command: "model-mix",
    title: "Model mix",
    description: "Models ranked by prompt count in the current scope over the last 30 days. For token volume, use the Token spend canvas.",
    steps: [
        {
            kind: "analytics_query", id: "q", spec: {
                root: PROMPT_ROOT,
                timeRange: days(30),
                output: {
                    dimensions: [{ field: p("DIMENSION_FIELD_PROMPT_MODEL"), outputAlias: "label" }],
                    metrics: [countPrompts("value")],
                },
                orderBy: desc("value"),
                limit: 15,
            },
        },
    ],
    render: { kind: "bars", labelField: "label", valueField: "value", format: "integer", maxItems: 15, empty: "No model activity in range" },
});

// ─────────────────────────────────────────────────────────────────────────
// B. CONCENTRATION & SPREAD — "how concentrated", "MCP tool sprawl" (scoped)
// ─────────────────────────────────────────────────────────────────────────

const topEndpoints = k({
    id: BUILTIN_KERNEL_IDS["top-endpoints"],
    command: "top-endpoints",
    title: "Busiest endpoints",
    description: "Endpoints ranked by prompt count in the current scope over the last 30 days. Select a bar to open an endpoint.",
    steps: [
        {
            kind: "analytics_query", id: "q", spec: {
                root: PROMPT_ROOT,
                timeRange: days(30),
                output: {
                    dimensions: [{ field: p("DIMENSION_FIELD_PROMPT_ENDPOINT_ID"), outputAlias: "id" }],
                    metrics: [countPrompts("value")],
                },
                orderBy: desc("value"),
                limit: 15,
            },
        },
    ],
    render: { kind: "bars", labelField: "id", valueField: "value", format: "integer", maxItems: 15, link: { dim: "endpoints", idField: "id" }, empty: "No endpoints in range" },
});

const toolLeaders = k({
    id: BUILTIN_KERNEL_IDS["tool-leaders"],
    command: "tool-leaders",
    title: "Tool & MCP-tool sprawl",
    description: "Tools ranked by distinct prompts in the current scope over the last 30 days, including MCP tools. Select a bar to open a tool.",
    steps: [
        {
            kind: "analytics_query", id: "q", spec: {
                root: PROMPT_ROOT,
                timeRange: days(30),
                output: {
                    // TOOL_CALL_NAMES is an Array(String) dim — the planner explodes it,
                    // so distinct logical IDs count prompts touching this tool
                    // (plain COUNT would over-report a prompt that calls N tools).
                    dimensions: [{ field: p("DIMENSION_FIELD_PROMPT_TOOL_CALL_NAMES"), outputAlias: "label" }],
                    metrics: [countPrompts("value")],
                },
                orderBy: desc("value"),
                limit: 15,
            },
        },
    ],
    render: { kind: "bars", labelField: "label", valueField: "value", format: "integer", maxItems: 15, link: { dim: "tools", idField: "label" }, empty: "No tool calls in range" },
});

const newModels = k({
    id: BUILTIN_KERNEL_IDS["new-models"],
    command: "new-models",
    title: "Newly-seen models",
    description: "Models ranked by their first prompt in the current scope during the last 90 days, newest first.",
    steps: [
        {
            kind: "analytics_query", id: "q", spec: {
                root: PROMPT_ROOT,
                timeRange: days(90),
                output: {
                    dimensions: [{ field: p("DIMENSION_FIELD_PROMPT_MODEL"), outputAlias: "model" }],
                    metrics: [
                        { outputAlias: "first_seen", computed: { field: p("DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP"), op: "AGGREGATE_OP_MIN" } },
                        countPrompts("prompts"),
                    ],
                },
                orderBy: [{ outputAlias: "first_seen", direction: "DIRECTION_DESC" }],
                limit: 20,
            },
        },
    ],
    render: {
        kind: "table",
        columns: [
            { id: "model", label: "Model" },
            { id: "first_seen", label: "First seen", format: "date", align: "right" },
            { id: "prompts", label: "Prompts", format: "integer", align: "right" },
        ],
        maxRows: 20,
        empty: "No models in range",
    },
});

// First-seen table over a 90d window — the "shadow AI" detection shape: an AI
// surface (provider / harness) ranked by when it FIRST appeared in the current
// scope, newest at the top, so an unsanctioned vendor or tool showing up is the
// top row. Prompt-rooted → narrows to the active scope.
const firstSeenKernel = (id, command, title, field, colLabel, noun) => k({
    id,
    command,
    title,
    description: `${noun} ranked by their first prompt in the current scope during the last 90 days, newest first.`,
    steps: [
        {
            kind: "analytics_query", id: "q", spec: {
                root: PROMPT_ROOT,
                timeRange: days(90),
                output: {
                    dimensions: [{ field: p(field), outputAlias: "name" }],
                    metrics: [
                        { outputAlias: "first_seen", computed: { field: p("DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP"), op: "AGGREGATE_OP_MIN" } },
                        countPrompts("prompts"),
                    ],
                },
                orderBy: [{ outputAlias: "first_seen", direction: "DIRECTION_DESC" }],
                limit: 20,
            },
        },
    ],
    render: {
        kind: "table",
        columns: [
            { id: "name", label: colLabel },
            { id: "first_seen", label: "First seen", format: "date", align: "right" },
            { id: "prompts", label: "Prompts", format: "integer", align: "right" },
        ],
        maxRows: 20,
        empty: "No data in range",
    },
});

const newProviders = firstSeenKernel(BUILTIN_KERNEL_IDS["new-providers"], "new-providers", "Newly-seen providers", "DIMENSION_FIELD_PROMPT_PROVIDER", "Provider", "Providers");
const newAgentSurfaces = firstSeenKernel(BUILTIN_KERNEL_IDS["new-agent-surfaces"], "new-agent-surfaces", "Newly-seen agent surfaces", "DIMENSION_FIELD_PROMPT_AGENT_TYPE", "Agent surface", "Agent surfaces");

// ─────────────────────────────────────────────────────────────────────────
// C. TOOLS & RESOURCES — what agents DO (tool-leaders, section B) and TOUCH.
//    Durable `security.*` detections in `signal_tags` are distinct from
//    functional extraction namespaces such as skill and memory. Browser-local
//    norms and intents are not server-queryable and are not built-in data.
// ─────────────────────────────────────────────────────────────────────────

const resourceLeaders = k({
    id: BUILTIN_KERNEL_IDS["resource-leaders"],
    command: "resource-leaders",
    title: "Files & resources touched",
    description: "Files and resource paths ranked by distinct prompts in the current scope over the last 30 days. Results may include Git references and MCP API paths.",
    steps: [
        {
            kind: "analytics_query", id: "q", spec: {
                root: PROMPT_ROOT,
                timeRange: days(30),
                output: {
                    // RESOURCE_PATHS is an Array(String) dim (the same family as
                    // TOOL_CALL_NAMES); the planner explodes it, so
                    // COUNT_DISTINCT(event_id) = "distinct prompts touching this path".
                    dimensions: [{ field: p("DIMENSION_FIELD_PROMPT_RESOURCE_PATHS"), outputAlias: "label" }],
                    metrics: [countPrompts("value")],
                },
                orderBy: desc("value"),
                limit: 15,
            },
        },
    ],
    render: { kind: "bars", labelField: "label", valueField: "value", format: "integer", maxItems: 15, empty: "No resource paths in range" },
});

// ─────────────────────────────────────────────────────────────────────────
// C2. NETWORK-EVIDENCE FOOTPRINT — AI-service observations captured on the
//     endpoint (DNS today), independent of prompt capture: the shadow-AI
//     reach a provider has across machines even where no prompts are seen.
//     Observation-rooted, so NOT scope-injectable (scopeMode:"global").
// ─────────────────────────────────────────────────────────────────────────

const obs = (field) => ({ stepAlias: "o", field });

const providerFootprint = k({
    id: BUILTIN_KERNEL_IDS["provider-footprint"],
    command: "provider-footprint",
    title: "Sanctioned vs. unsanctioned model providers",
    description: "Model providers reached from company endpoints in the last 30 days.",
    scopeMode: "global",
    steps: [
        {
            kind: "analytics_query", id: "q", spec: {
                root: {
                    alias: "o",
                    entity: "ENTITY_TYPE_AI_SERVICE_OBSERVATION",
                    stepFilter: [{
                        pred: {
                            field: obs("DIMENSION_FIELD_AI_SERVICE_OBSERVATION_EVIDENCE_KIND"),
                            op: "OPERATOR_EQ",
                            value: "dns_query",
                        },
                    }],
                },
                timeRange: days(30),
                output: {
                    dimensions: [
                        { field: obs("DIMENSION_FIELD_AI_SERVICE_OBSERVATION_PROVIDER_TAG"), outputAlias: "label" },
                        // Tenant policy, derived server-side per provider, so this
                        // adds a column rather than splitting the grouping. Providers
                        // set to `ignored` never arrive here at all — the planner
                        // drops them before the scan.
                        { field: obs("DIMENSION_FIELD_AI_SERVICE_OBSERVATION_DISPOSITION"), outputAlias: "disposition" },
                    ],
                    metrics: [{
                        outputAlias: "value",
                        computed: { field: obs("DIMENSION_FIELD_AI_SERVICE_OBSERVATION_ENDPOINT_ID"), op: "AGGREGATE_OP_COUNT_DISTINCT" },
                    }],
                },
                // Tie-break on the label: endpoint counts tie constantly (every
                // provider reached by one machine scores 1), and an unstable sort
                // makes it arbitrary which providers land past `maxItems`.
                orderBy: [...desc("value"), { outputAlias: "label", direction: "DIRECTION_ASC" }],
                // Well above the provider catalog so the panel sees the whole
                // tenant footprint; the renderer folds the tail into "Other"
                // rather than the query hiding it.
                limit: 100,
            },
        },
    ],
    // Split by policy so each category totals itself: a provider's percentage
    // is its share of the unsanctioned (or sanctioned) footprint, which is the
    // comparison a reviewer is actually making. Unsanctioned leads because it
    // is the population being triaged.
    render: {
        kind: "composition",
        labelField: "label",
        valueField: "value",
        splitField: "disposition",
        splitOrder: ["unsanctioned", "sanctioned"],
        splitPalettes: {
            unsanctioned: [
                "#7f2f2a", "#934039", "#a65049", "#b86159", "#c8746c",
                "#d4867f", "#df9a92", "#e7ada6", "#edc0ba", "#f2d2cd",
            ],
            sanctioned: [
                "#28583d", "#37694b", "#477a5a", "#588b69", "#6a9b79",
                "#7eab8a", "#93bb9c", "#a9caae", "#bfd9c1", "#d4e7d5",
            ],
        },
        sharedScale: true,
        valueLabel: "endpoint exposures",
        maxItems: 20,
        empty: "No model-provider activity observed in range",
    },
});

// ─────────────────────────────────────────────────────────────────────────
// C3. LOCAL-AI EVIDENCE — local AI software seen ON the endpoint, which leaves
//     no provider domain to resolve. The three kernels answer "how many",
//     "how do we know", and "which machines" over the same rows.
//     Observation-rooted, so NOT scope-injectable (scopeMode:"global").
//
//     No verdict here on purpose: these show evidence and counts, never a
//     confidence tier. Scoring belongs in the analytics planner, where it can
//     fuse classes per endpoint; a tier invented in the view would train
//     readers on a judgement the backend does not make.
// ─────────────────────────────────────────────────────────────────────────

/// Local inference carries the reserved `local` provider tag, so one predicate
/// separates it from every network-resolved provider.
const localEvidence = [{
    pred: {
        field: obs("DIMENSION_FIELD_AI_SERVICE_OBSERVATION_PROVIDER_TAG"),
        op: "OPERATOR_EQ",
        value: "local",
    },
}];

/// Local AI software is counted by the program hosting the evidence, not by the
/// rule or library that matched: the agent attributes a runtime's worker
/// children to the runtime that launched them, so one install is one product.
/// The summary and the detail share this filter so their counts agree.
const LOCAL_AI_HOST = obs("DIMENSION_FIELD_AI_SERVICE_OBSERVATION_HOST_PROCESS_NAME");
const localSoftwareEvidence = [{
    andExpr: {
        children: [
            ...localEvidence,
            { pred: { field: LOCAL_AI_HOST, op: "OPERATOR_NE", value: "" } },
        ],
    },
}];

const localAiSummary = k({
    id: BUILTIN_KERNEL_IDS["local-ai-summary"],
    command: "local-ai-summary",
    title: "Local AI presence",
    description: "Endpoint telemetry reveals local AI software that cloud-provider monitoring cannot see.",
    scopeMode: "global",
    steps: [{
        kind: "analytics_query", id: "q", spec: {
            root: {
                alias: "o",
                entity: "ENTITY_TYPE_AI_SERVICE_OBSERVATION",
                stepFilter: localSoftwareEvidence,
            },
            timeRange: days(30),
            output: { metrics: [
                {
                    outputAlias: "local_endpoints",
                    computed: { field: obs("DIMENSION_FIELD_AI_SERVICE_OBSERVATION_ENDPOINT_ID"), op: "AGGREGATE_OP_COUNT_DISTINCT" },
                },
                {
                    outputAlias: "local_software",
                    computed: { field: LOCAL_AI_HOST, op: "AGGREGATE_OP_COUNT_DISTINCT" },
                },
            ] },
        },
    }],
    render: {
        kind: "kpis",
        tiles: [
            { label: "Endpoints", valueField: "local_endpoints", format: "integer", sublabel: "with local AI software" },
            { label: "Software", valueField: "local_software", format: "integer", sublabel: "unique local AI products" },
        ],
    },
});

const localAiEvidence = k({
    id: BUILTIN_KERNEL_IDS["local-ai-evidence"],
    command: "local-ai-evidence",
    title: "How local AI was detected (30d)",
    description: "Endpoints per evidence class over the last 30 days. The classes are not interchangeable: `gpu_inference` is a library that reached an accelerator, `module_load` an inference library mapped into a process, `process_execution` a known runtime that merely started. A machine appears under each class that saw it.",
    scopeMode: "global",
    steps: [
        {
            kind: "analytics_query", id: "q", spec: {
                root: {
                    alias: "o",
                    entity: "ENTITY_TYPE_AI_SERVICE_OBSERVATION",
                    stepFilter: localEvidence,
                },
                timeRange: days(30),
                output: {
                    dimensions: [{ field: obs("DIMENSION_FIELD_AI_SERVICE_OBSERVATION_EVIDENCE_KIND"), outputAlias: "label" }],
                    metrics: [{
                        outputAlias: "value",
                        computed: { field: obs("DIMENSION_FIELD_AI_SERVICE_OBSERVATION_ENDPOINT_ID"), op: "AGGREGATE_OP_COUNT_DISTINCT" },
                    }],
                },
                // Ties broken by label so the segment order is stable between
                // refreshes rather than left to the engine's row order.
                orderBy: [...desc("value"), { outputAlias: "label", direction: "DIRECTION_ASC" }],
                limit: 20,
            },
        },
    ],
    render: { kind: "composition", labelField: "label", valueField: "value", maxItems: 20, empty: "No on-endpoint AI evidence in range" },
});

const localAiDetail = k({
    id: BUILTIN_KERNEL_IDS["local-ai-detail"],
    command: "local-ai-detail",
    title: "Local AI software by endpoint",
    description: "Local AI software observed on endpoints in the last 30 days, including runtimes that started without proof of inference. Software is named by the program running it; helper processes a runtime starts are listed under that runtime.",
    scopeMode: "global",
    steps: [{
        kind: "analytics_query", id: "q", spec: {
            root: {
                alias: "o",
                entity: "ENTITY_TYPE_AI_SERVICE_OBSERVATION",
                stepFilter: localSoftwareEvidence,
            },
            timeRange: days(30),
            output: {
                dimensions: [
                    { field: obs("DIMENSION_FIELD_AI_SERVICE_OBSERVATION_ENDPOINT_ID"), outputAlias: "endpoint" },
                    { field: LOCAL_AI_HOST, outputAlias: "software" },
                ],
                metrics: [{
                    outputAlias: "last_seen",
                    computed: { field: obs("DIMENSION_FIELD_AI_SERVICE_OBSERVATION_OBSERVED_AT"), op: "AGGREGATE_OP_MAX" },
                }],
            },
            orderBy: [
                ...desc("last_seen"),
                { outputAlias: "endpoint", direction: "DIRECTION_ASC" },
                { outputAlias: "software", direction: "DIRECTION_ASC" },
            ],
            limit: 200,
        },
    }],
    renderSource: "q",
    render: {
        kind: "table",
        columns: [
            { id: "endpoint", label: "Endpoint", link: { dim: "endpoints", idField: "endpoint" } },
            { id: "software", label: "Local AI software" },
            { id: "last_seen", label: "Last active", format: "timestamp", align: "right" },
        ],
        defaultSort: { id: "last_seen", dir: "desc" },
        maxRows: 10,
        empty: "No local AI software observed in range",
    },
});

// Process-execution is the one local-AI evidence class that carries no library
// of its own -- the executed image is the whole signal -- so `local-ai-detail`,
// keyed on the module column, shows it only as a runtime with an empty library.
// This kernel is the process arm on its own terms: which recognized runtime
// BINARY started, on which endpoint, when, and the scrubbed command line it
// launched with. `command_line` stays Default sensitivity (readable with
// tenant.endpoints.read) until the endpoint resource gains an approved
// sensitive-read permission model.
const localProcessExec = [{
    andExpr: {
        children: [
            { pred: { field: obs("DIMENSION_FIELD_AI_SERVICE_OBSERVATION_PROVIDER_TAG"), op: "OPERATOR_EQ", value: "local" } },
            { pred: { field: obs("DIMENSION_FIELD_AI_SERVICE_OBSERVATION_EVIDENCE_KIND"), op: "OPERATOR_EQ", value: "process_execution" } },
        ],
    },
}];

const localAiRuntimeExec = k({
    id: BUILTIN_KERNEL_IDS["local-ai-runtime-exec"],
    command: "local-ai-runtime-exec",
    title: "Local runtimes executed (30d)",
    description: "Endpoints where a recognized local-LLM runtime binary started during the last 30 days, most recent first. This is the weakest local-AI signal: it shows a runtime ran, not that a model loaded or an API was served, so read a row as a lead to confirm against the other evidence classes, not a verdict. One row per endpoint; open a row to fetch exact execution counts for only that machine's runtime-image and command-line breakdown. Tenant-wide (an observation root is not scope-injectable).",
    scopeMode: "global",
    steps: [
        {
            // Parent: one recency-ranked row per endpoint. Fleet-wide distinct
            // sets would still grow with every execution, even inside an
            // aggregate projection, so runtime/image/execution counts live in
            // the endpoint-filtered detail only.
            kind: "analytics_query", id: "q", spec: {
                root: { alias: "o", entity: "ENTITY_TYPE_AI_SERVICE_OBSERVATION_ENDPOINT_RECENCY", stepFilter: localProcessExec },
                // The recency access role applies this bound after combining
                // each endpoint's projection states with max(observed_at).
                timeRange: days(30),
                output: {
                    dimensions: [
                        { field: obs("DIMENSION_FIELD_AI_SERVICE_OBSERVATION_ENDPOINT_ID"), outputAlias: "endpoint" },
                    ],
                    metrics: [
                        {
                            outputAlias: "last_seen",
                            computed: { field: obs("DIMENSION_FIELD_AI_SERVICE_OBSERVATION_OBSERVED_AT"), op: "AGGREGATE_OP_MAX" },
                        },
                    ],
                },
                // Recency-ranked: this is a "what ran lately" view, so the newest
                // execution leads. Endpoint id breaks ties for a stable order.
                orderBy: [...desc("last_seen"), { outputAlias: "endpoint", direction: "DIRECTION_ASC" }],
                limit: 200,
            },
        },
    ],
    renderSource: "q",
    render: {
        kind: "table",
        columns: [
            { id: "endpoint", label: "Endpoint", link: { dim: "endpoints", idField: "endpoint" } },
            { id: "last_seen", label: "Last seen", format: "timestamp", align: "right", width: 170 },
        ],
        detailField: "detail",
        // Explicit row expansion is the only trigger for this query. `$row`
        // markers are resolved by the table detail loader, after the parent
        // result exists; they are not kernel parameters and are never sent to
        // the analytics API verbatim.
        detailQuery: {
            spec: {
                root: {
                    alias: "o",
                    entity: "ENTITY_TYPE_AI_SERVICE_OBSERVATION",
                    stepFilter: [
                        ...localProcessExec,
                        {
                            pred: {
                                field: obs("DIMENSION_FIELD_AI_SERVICE_OBSERVATION_ENDPOINT_ID"),
                                op: "OPERATOR_EQ",
                                value: { $row: "endpoint" },
                            },
                        },
                    ],
                },
                timeRange: days(30),
                output: {
                    dimensions: [
                        { field: obs("DIMENSION_FIELD_AI_SERVICE_OBSERVATION_PROCESS_IMAGE"), outputAlias: "image" },
                        { field: obs("DIMENSION_FIELD_AI_SERVICE_OBSERVATION_RECOGNITION_RULE_ID"), outputAlias: "runtime" },
                        { field: obs("DIMENSION_FIELD_AI_SERVICE_OBSERVATION_COMMAND_LINE"), outputAlias: "command" },
                    ],
                    metrics: [
                        {
                            outputAlias: "executions",
                            computed: { field: obs("DIMENSION_FIELD_AI_SERVICE_OBSERVATION_EVENT_ID"), op: "AGGREGATE_OP_COUNT_DISTINCT" },
                        },
                        {
                            outputAlias: "last_seen",
                            computed: { field: obs("DIMENSION_FIELD_AI_SERVICE_OBSERVATION_OBSERVED_AT"), op: "AGGREGATE_OP_MAX" },
                        },
                    ],
                },
                orderBy: [
                    ...desc("executions"),
                    { outputAlias: "image", direction: "DIRECTION_ASC" },
                ],
                limit: 200,
            },
        },
        detailColumns: [
            // The two content columns claim the free space (command line most);
            // rule/executions/last-seen are pinned narrow -- they carry a short,
            // low-variance value. Last seen is the exception now that it shows
            // date + time, so it gets a wider fixed width for both.
            { id: "image", label: "Runtime image", weight: 2 },
            { id: "command", label: "Command line", emptyText: "N/A", weight: 4 },
            // Pinned narrow, but each fixed width must clear its own uppercase,
            // letter-spaced header or the title truncates (e.g. "EXECUTI...").
            { id: "runtime", label: "Rule", width: 160 },
            { id: "executions", label: "Executions", format: "integer", align: "right", width: 108 },
            { id: "last_seen", label: "Last seen", format: "timestamp", align: "right", width: 170 },
        ],
        defaultSort: { id: "last_seen", dir: "desc" },
        maxRows: 200,
        empty: "No local runtime executions in range",
    },
});

// ─────────────────────────────────────────────────────────────────────────
// D. TOKEN CAPITAL — "where token capital went". TOKEN-rooted, so NOT
//    scope-injectable: these run tenant-wide everywhere (scopeMode:"global").
// ─────────────────────────────────────────────────────────────────────────

const tokenSummary = k({
    id: BUILTIN_KERNEL_IDS["token-summary"],
    command: "token-summary",
    title: "Token volume (7d)",
    description: "Tenant-wide token volume over the last 7 days: net-new input + output, plus Anthropic cache reads and reasoning tokens. Token accounting is tenant-wide (not scoped to the current drilldown).",
    scopeMode: "global",
    steps: [
        {
            kind: "analytics_query", id: "q", spec: {
                root: { alias: "tk", entity: "ENTITY_TYPE_TOKEN" },
                timeRange: days(7),
                output: { metrics: [inputMetric, outputMetric, cacheReadMetric, reasoningMetric] },
            },
        },
        { kind: "python", id: "pick", inputs: { rows: "q" }, code: PICK_FIRST_ROW },
    ],
    renderSource: "pick",
    render: {
        kind: "kpis",
        tiles: [
            { label: "Input", valueField: "input_tokens", format: "integer" },
            { label: "Output", valueField: "output_tokens", format: "integer" },
            { label: "Cache reads", valueField: "cache_read_tokens", format: "integer" },
            { label: "Reasoning", valueField: "reasoning_tokens", format: "integer" },
        ],
    },
});

const tokenByModel = k({
    id: BUILTIN_KERNEL_IDS["token-by-model"],
    command: "token-by-model",
    title: "Token spend by model",
    description: "Tenant-wide input, output, cache-read, and reasoning tokens by model over the last 7 days, ranked by output.",
    scopeMode: "global",
    steps: [
        {
            kind: "analytics_query", id: "q", spec: {
                root: { alias: "tk", entity: "ENTITY_TYPE_TOKEN" },
                timeRange: days(7),
                output: {
                    dimensions: [{ field: tk("DIMENSION_FIELD_TOKEN_MODEL"), outputAlias: "model" }],
                    metrics: [inputMetric, outputMetric, cacheReadMetric, reasoningMetric],
                },
                orderBy: [{ outputAlias: "output_tokens", direction: "DIRECTION_DESC" }],
                limit: 15,
            },
        },
    ],
    render: {
        kind: "table",
        columns: [
            { id: "model", label: "Model" },
            { id: "input_tokens", label: "Input", format: "integer", align: "right" },
            { id: "output_tokens", label: "Output", format: "integer", align: "right" },
            { id: "cache_read_tokens", label: "Cache reads", format: "integer", align: "right" },
            { id: "reasoning_tokens", label: "Reasoning", format: "integer", align: "right" },
        ],
        maxRows: 15,
        empty: "No token data in range",
    },
});

const tokenBySurface = k({
    id: BUILTIN_KERNEL_IDS["token-by-surface"],
    command: "token-by-surface",
    title: "Token spend by client surface",
    description: "Tenant-wide output-token volume by client surface over the last 7 days.",
    scopeMode: "global",
    steps: [
        {
            kind: "analytics_query", id: "q", spec: {
                root: { alias: "tk", entity: "ENTITY_TYPE_TOKEN" },
                timeRange: days(7),
                output: {
                    dimensions: [{ field: tk("DIMENSION_FIELD_TOKEN_CLIENT_SURFACE"), outputAlias: "label" }],
                    metrics: [{ outputAlias: "value", standard: { stepAlias: "tk", metric: "METRIC_TYPE_TOKEN_SUM_OUTPUT_TOKENS" } }],
                },
                orderBy: desc("value"),
                limit: 12,
            },
        },
    ],
    render: { kind: "bars", labelField: "label", valueField: "value", format: "integer", maxItems: 12, empty: "No token data in range" },
});

const securityActivitySummary = k({
    id: BUILTIN_KERNEL_IDS["security-activity-summary"],
    command: "security-activity-summary",
    title: "AI coverage",
    description: "Total endpoints, the subset with installed AI agents, and the subset that produced AI prompts in the last seven days.",
    scopeMode: "global",
    steps: [
        {
            kind: "analytics_query", id: "total", spec: {
                root: {
                    alias: "e",
                    entity: "ENTITY_TYPE_ENDPOINT",
                    stepFilter: REAL_ENDPOINT_FILTER,
                },
                timeRange: days(30),
                output: { metrics: [{
                    outputAlias: "total_endpoints",
                    computed: {
                        field: {
                            stepAlias: "e",
                            field: "DIMENSION_FIELD_ENDPOINT_ENDPOINT_ID",
                        },
                        op: "AGGREGATE_OP_COUNT_DISTINCT",
                    },
                }] },
            },
        },
        {
            kind: "analytics_query", id: "equipped", spec: {
                root: {
                    alias: "e",
                    entity: "ENTITY_TYPE_ENDPOINT",
                    stepFilter: REAL_ENDPOINT_FILTER,
                    children: [{
                        edge: "EDGE_TYPE_ENDPOINT_HAS_AGENT_INVENTORY",
                        mode: "JOIN_MODE_INNER",
                        target: { alias: "inv", entity: "ENTITY_TYPE_AGENT_INVENTORY" },
                    }],
                },
                timeRange: days(30),
                output: { metrics: [{
                    outputAlias: "endpoints_with_ai",
                    computed: {
                        field: { stepAlias: "e", field: "DIMENSION_FIELD_ENDPOINT_ENDPOINT_ID" },
                        op: "AGGREGATE_OP_COUNT_DISTINCT",
                    },
                }] },
            },
        },
        {
            kind: "analytics_query", id: "active", spec: {
                root: {
                    alias: "p",
                    entity: "ENTITY_TYPE_PROMPT",
                    children: [{
                        edge: "EDGE_TYPE_PROMPT_ON_ENDPOINT",
                        mode: "JOIN_MODE_INNER",
                        target: {
                            alias: "e",
                            entity: "ENTITY_TYPE_ENDPOINT",
                            stepFilter: REAL_ENDPOINT_FILTER,
                        },
                    }],
                },
                timeRange: days(7),
                output: { metrics: [distinct("ai_active_endpoints", "DIMENSION_FIELD_PROMPT_ENDPOINT_ID")] },
            },
        },
        {
            kind: "python",
            id: "merge",
            inputs: { total: "total", equipped: "equipped", active: "active" },
            code: `values = {}
for name in ("total", "equipped", "active"):
    rows = inputs.get(name) or [{}]
    values.update(rows[0] if rows else {})
result = values
`,
        },
    ],
    renderSource: "merge",
    render: {
        kind: "kpis",
        tiles: [
            { label: "Total endpoints", valueField: "total_endpoints", format: "integer" },
            { label: "With AI agents", valueField: "endpoints_with_ai", format: "integer" },
            { label: "AI-active", valueField: "ai_active_endpoints", format: "integer", sublabel: "last 7 days" },
        ],
        empty: "No endpoint coverage data",
    },
});

const securityVolumeSummary = k({
    id: BUILTIN_KERNEL_IDS["security-volume-summary"],
    command: "security-volume-summary",
    title: "AI activity",
    description: "Traces captured and capture records processed in the last seven days.",
    steps: [
        {
            kind: "analytics_query", id: "q", spec: {
                root: PROMPT_ROOT,
                timeRange: days(7),
                output: { metrics: [
                    distinct("traces", "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID"),
                    distinct("prompts_processed", "DIMENSION_FIELD_PROMPT_EVENT_ID"),
                ] },
            },
        },
        { kind: "python", id: "pick", inputs: { rows: "q" }, code: PICK_FIRST_ROW },
    ],
    renderSource: "pick",
    render: { kind: "kpis", tiles: [
        { label: "Traces", valueField: "traces", format: "integer", sublabel: "last 7 days" },
        { label: "Capture records processed", valueField: "prompts_processed", format: "integer", sublabel: "last 7 days" },
    ] },
});

const installedAgentFootprint = k({
    id: BUILTIN_KERNEL_IDS["installed-agent-footprint"],
    command: "installed-agent-footprint",
    title: "Installed AI agents",
    description: "Current endpoint inventory by installed AI agent. The query carries the analytics API's required 30-day window; the inventory itself is current and tenant-wide. Counts distinct endpoints; legacy agent names remain until those endpoints report updated inventory.",
    scopeMode: "global",
    steps: [{
        kind: "analytics_query", id: "q", spec: {
            root: { alias: "inv", entity: "ENTITY_TYPE_AGENT_INVENTORY" },
            timeRange: days(30),
            output: {
                dimensions: [{ field: inv("DIMENSION_FIELD_AGENT_INVENTORY_AGENT_NAME"), outputAlias: "label" }],
                metrics: [{
                    outputAlias: "value",
                    computed: {
                        field: inv("DIMENSION_FIELD_AGENT_INVENTORY_ENDPOINT_ID"),
                        op: "AGGREGATE_OP_COUNT_DISTINCT",
                    },
                }],
            },
            orderBy: [
                ...desc("value"),
                { outputAlias: "label", direction: "DIRECTION_ASC" },
            ],
            limit: 10,
        },
    }],
    render: {
        kind: "bars",
        labelField: "label",
        valueField: "value",
        format: "integer",
        maxItems: 10,
        empty: "No installed AI agents in inventory",
    },
});

const agentUseConcentration = k({
    id: BUILTIN_KERNEL_IDS["agent-use-concentration"],
    command: "agent-use-concentration",
    title: "AI activity by provider",
    description: "Prompt volume attributed to each AI provider in the last seven days.",
    steps: [
        {
            kind: "analytics_query", id: "q", spec: {
                root: PROMPT_ROOT,
                timeRange: days(7),
                filter: { pred: {
                    field: p("DIMENSION_FIELD_PROMPT_PROVIDER"),
                    op: "OPERATOR_NE",
                    value: "",
                } },
                output: {
                    dimensions: [{ field: p("DIMENSION_FIELD_PROMPT_PROVIDER"), outputAlias: "provider" }],
                    metrics: [countPrompts("prompts")],
                },
                orderBy: [
                    ...desc("prompts"),
                    { outputAlias: "provider", direction: "DIRECTION_ASC" },
                ],
                limit: 8,
            },
        },
        {
            kind: "python",
            id: "labels",
            inputs: { rows: "q" },
            code: `labels = {
    "anthropic": "Claude / Anthropic",
    "openai": "ChatGPT, Codex / OpenAI",
    "google": "Gemini / Google",
    "microsoft": "Copilot / Microsoft",
    "cursor": "Cursor",
    "xai": "xAI",
    "deepseek": "DeepSeek",
    "local": "Local AI",
}
result = []
for row in inputs.get("rows") or []:
    item = dict(row)
    provider = str(item.get("provider") or "")
    item["provider_label"] = labels.get(provider.lower(), provider.replace("_", " ").replace("-", " ").title())
    result.append(item)
`,
        },
    ],
    renderSource: "labels",
    render: {
        kind: "bars",
        labelField: "provider_label",
        valueField: "prompts",
        format: "integer",
        maxItems: 8,
        color: "var(--blue)",
        link: { dim: "providers", idField: "provider", labelField: "provider_label" },
        empty: "No attributed AI activity in the last seven days",
    },
});

const observedWorkClusters = k({
    id: BUILTIN_KERNEL_IDS["observed-work-clusters"],
    command: "observed-work-clusters",
    title: "What AI is used for",
    description: "The work categories engaging the most traces and endpoints in the last 30 days.",
    steps: [
        {
            kind: "analytics_query", id: "q", spec: {
            root: {
                alias: "p",
                entity: "ENTITY_TYPE_PROMPT",
                children: [{
                    edge: "EDGE_TYPE_PROMPT_HAS_EVENT_CLUSTER",
                    mode: "JOIN_MODE_INNER",
                    target: {
                        alias: "ec",
                        entity: "ENTITY_TYPE_ILO_EVENT_CLUSTER",
                        stepFilter: [{ pred: {
                            field: ec("DIMENSION_FIELD_ILO_EVENT_CLUSTER_CLUSTER_ID"),
                            op: "OPERATOR_GT",
                            value: 0,
                        } }],
                        children: [{
                            edge: "EDGE_TYPE_EVENT_CLUSTER_IN_CLUSTER",
                            mode: "JOIN_MODE_LEFT",
                            target: { alias: "c", entity: "ENTITY_TYPE_ILO_CLUSTER" },
                        }],
                    },
                }],
            },
            timeRange: days(30),
            output: {
                dimensions: [
                    { field: ec("DIMENSION_FIELD_ILO_EVENT_CLUSTER_CLUSTER_ID"), outputAlias: "cluster_id" },
                    { field: cluster("DIMENSION_FIELD_ILO_CLUSTER_CLUSTER_NAME"), outputAlias: "name" },
                ],
                metrics: [
                    countPrompts("traces"),
                    distinct("endpoints", "DIMENSION_FIELD_PROMPT_ENDPOINT_ID"),
                ],
            },
            orderBy: [
                ...desc("traces"),
                { outputAlias: "cluster_id", direction: "DIRECTION_ASC" },
            ],
            limit: 8,
            },
        },
        {
            kind: "python",
            id: "rows",
            inputs: { rows: "q" },
            code: `result = []
for row in inputs.get("rows") or []:
    item = dict(row)
    raw_name = str(item.get("name") or "")
    item["name"] = raw_name.replace("_", " ").replace("-", " ").title() or "Unlabeled work category"
    endpoints = int(item.get("endpoints") or 0)
    noun = "endpoint" if endpoints == 1 else "endpoints"
    item["reach"] = f"{endpoints:,} {noun} engaged"
    result.append(item)
`,
        },
    ],
    renderSource: "rows",
    render: {
        kind: "rows",
        primaryField: "name",
        secondaryField: "reach",
        rightField: "traces",
        rightFormat: "integer",
        link: { dim: "clusters", idField: "cluster_id", labelField: "name" },
        maxRows: 8,
        empty: "No clustered traces in range",
    },
});

const capabilitySignalQuery = (namespace, { excludeMemoryIndex = false } = {}) => {
    const namespaceFilter = { pred: {
        field: st("DIMENSION_FIELD_SIGNAL_TAGS_NAMESPACE"),
        op: "OPERATOR_EQ",
        value: namespace,
    } };
    const filter = excludeMemoryIndex
        ? { andExpr: { children: [
            namespaceFilter,
            { pred: {
                field: st("DIMENSION_FIELD_SIGNAL_TAGS_TAG"),
                op: "OPERATOR_NE",
                value: "MEMORY",
            } },
        ] } }
        : namespaceFilter;
    return {
        root: {
            alias: "p",
            entity: "ENTITY_TYPE_PROMPT",
            children: [{
                edge: "EDGE_TYPE_PROMPT_HAS_SIGNAL_TAGS",
                mode: "JOIN_MODE_INNER",
                target: { alias: "st", entity: "ENTITY_TYPE_SIGNAL_TAGS" },
            }],
        },
        timeRange: days(30),
        filter,
        output: {
            dimensions: [{
                field: st("DIMENSION_FIELD_SIGNAL_TAGS_TAG"),
                outputAlias: "capability",
            }],
            metrics: [
                countPrompts("affected_traces"),
                distinct("endpoints", "DIMENSION_FIELD_PROMPT_ENDPOINT_ID"),
                {
                    outputAlias: "last_seen",
                    computed: {
                        field: p("DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP"),
                        op: "AGGREGATE_OP_MAX",
                    },
                },
            ],
        },
        orderBy: [
            ...desc("affected_traces"),
            { outputAlias: "capability", direction: "DIRECTION_ASC" },
        ],
        limit: 4,
    };
};

const MCP_CARD_ROWS = `servers = {}
for row in inputs.get("rows") or []:
    name = str(row.get("tool_name") or "")
    parts = name.split("__")
    if len(parts) < 3 or parts[0].lower() != "mcp":
        continue
    server = parts[1]
    entry = servers.setdefault(server, {"uses": 0, "tools": set()})
    entry["uses"] += int(row.get("uses") or 0)
    entry["tools"].add("__".join(parts[2:]))
result = []
for server, entry in servers.items():
    tools = len(entry["tools"])
    result.append({
        "capability": server,
        "capability_label": server.replace("_", " ").replace("-", " ").title(),
        "reach": f"{tools:,} {'tool' if tools == 1 else 'tools'}",
        "uses": entry["uses"],
    })
result.sort(key=lambda row: (-row["uses"], row["capability"]))
result = result[:4]
`;

const mcpServerPrevalence = k({
    id: BUILTIN_KERNEL_IDS["mcp-server-prevalence"],
    command: "mcp-server-prevalence",
    title: "MCP servers",
    description: "MCP tool uses in the last 30 days. Each distinct tool used in a prompt counts once.",
    steps: [
        {
            kind: "analytics_query", id: "q", spec: {
                root: PROMPT_ROOT,
                timeRange: days(30),
                filter: { pred: {
                    field: p("DIMENSION_FIELD_PROMPT_TOOL_CALL_NAMES"),
                    op: "OPERATOR_LIKE",
                    value: "mcp\\_\\_%",
                } },
                output: {
                    dimensions: [{
                        field: p("DIMENSION_FIELD_PROMPT_TOOL_CALL_NAMES"),
                        outputAlias: "tool_name",
                    }],
                    metrics: [countPrompts("uses")],
                },
                orderBy: [
                    ...desc("uses"),
                    { outputAlias: "tool_name", direction: "DIRECTION_ASC" },
                ],
                limit: 500,
            },
        },
        { kind: "python", id: "rows", inputs: { rows: "q" }, code: MCP_CARD_ROWS },
    ],
    renderSource: "rows",
    render: {
        kind: "rows",
        primaryField: "capability_label",
        secondaryField: "reach",
        rightField: "uses",
        rightFormat: "integer",
        link: { dim: "mcp_servers", idField: "capability", labelField: "capability_label" },
        maxRows: 4,
        empty: "No MCP servers used in the last 30 days",
    },
});

const CAPABILITY_CARD_ROWS = `result = []
for row in inputs.get("rows") or []:
    item = dict(row)
    capability = str(item.get("capability") or "")
    item["capability_label"] = capability.replace("_", " ").replace("-", " ").title()
    endpoints = int(item.get("endpoints") or 0)
    noun = "endpoint" if endpoints == 1 else "endpoints"
    item["reach"] = f"{endpoints:,} {noun}"
    result.append(item)
`;

const capabilitySignalCard = ({ id, command, title, namespace, dim, excludeMemoryIndex = false }) => k({
    id,
    command,
    title,
    description: `${title} observed most often in traces over the last 30 days.`,
    steps: [
        { kind: "analytics_query", id: "q", spec: capabilitySignalQuery(namespace, { excludeMemoryIndex }) },
        { kind: "python", id: "rows", inputs: { rows: "q" }, code: CAPABILITY_CARD_ROWS },
    ],
    renderSource: "rows",
    render: {
        kind: "rows",
        primaryField: "capability_label",
        secondaryField: "reach",
        rightField: "affected_traces",
        rightFormat: "integer",
        link: { dim, idField: "capability", labelField: "capability_label" },
        maxRows: 4,
        empty: `No ${title.toLowerCase()} observed in the last 30 days`,
    },
});

const skillPrevalence = capabilitySignalCard({
    id: BUILTIN_KERNEL_IDS["skill-prevalence"],
    command: "skill-prevalence",
    title: "Skills",
    namespace: "skill",
    dim: "skills",
});

const memoryPrevalence = capabilitySignalCard({
    id: BUILTIN_KERNEL_IDS["memory-prevalence"],
    command: "memory-prevalence",
    title: "Memories",
    namespace: "memory",
    dim: "memories",
    excludeMemoryIndex: true,
});

const loudestSignals = k({
    id: BUILTIN_KERNEL_IDS["loudest-signals"],
    command: "loudest-signals",
    title: "Most triggered Signals",
    description: "Signals triggered most often in the last seven days and the endpoints involved.",
    steps: [
        {
            kind: "analytics_query", id: "q", spec: {
            root: {
                alias: "p",
                entity: "ENTITY_TYPE_PROMPT",
                children: [{
                    edge: "EDGE_TYPE_PROMPT_HAS_SIGNAL_TAGS",
                    mode: "JOIN_MODE_INNER",
                    target: { alias: "st", entity: "ENTITY_TYPE_SIGNAL_TAGS" },
                }],
            },
            timeRange: days(7),
            filter: { pred: {
                field: st("DIMENSION_FIELD_SIGNAL_TAGS_NAMESPACE"),
                op: "OPERATOR_LIKE",
                value: "security.%",
            } },
            output: {
                dimensions: [{
                    field: st("DIMENSION_FIELD_SIGNAL_TAGS_NAMESPACE"),
                    outputAlias: "signal",
                }],
                metrics: [
                    {
                        outputAlias: "signal_events",
                        computed: {
                            field: st("DIMENSION_FIELD_SIGNAL_TAGS_EVENT_ID"),
                            op: "AGGREGATE_OP_COUNT_DISTINCT",
                        },
                    },
                    distinct("endpoints", "DIMENSION_FIELD_PROMPT_ENDPOINT_ID"),
                    {
                        outputAlias: "last_seen",
                        computed: {
                            field: p("DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP"),
                            op: "AGGREGATE_OP_MAX",
                        },
                    },
                ],
            },
            orderBy: [
                ...desc("signal_events"),
                { outputAlias: "signal", direction: "DIRECTION_ASC" },
            ],
            limit: 8,
            },
        },
        {
            kind: "python",
            id: "labels",
            inputs: { rows: "q" },
            code: `command_overrides = ${DETECTION_COMMAND_OVERRIDES_PY}
labels = {
    "credscan": "Cred scan",
    "package_install": "Package install",
}
result = []
for row in inputs.get("rows") or []:
    item = dict(row)
    raw = str(item.get("signal") or "")
    key = raw.removeprefix("security.")
    item["signal_command"] = command_overrides.get(raw, key.replace("_", "-"))
    item["signal_label"] = labels.get(key, key.replace("_", " ").capitalize())
    result.append(item)
`,
        },
    ],
    renderSource: "labels",
    render: {
        kind: "table",
        columns: [
            { id: "signal_label", label: "Signal", link: { dim: "signals", idField: "signal_command", labelField: "signal_label" } },
            { id: "signal_events", label: "Events", format: "integer", align: "right" },
            { id: "endpoints", label: "Endpoints", format: "integer", align: "right" },
            { id: "last_seen", label: "Last triggered", format: "timestamp", align: "right" },
        ],
        defaultSort: { id: "signal_events", dir: "desc" },
        maxRows: 8,
        empty: "No Signals triggered in the last seven days",
    },
});

const endpointsWithSignals = k({
    id: BUILTIN_KERNEL_IDS["endpoints-with-signals"],
    command: "endpoints-with-signals",
    title: "Endpoints with the most Signals",
    description: "The ten endpoints associated with the most distinct Signal events in the last seven days.",
    steps: [{
        kind: "analytics_query", id: "q", spec: {
            root: {
                alias: "p",
                entity: "ENTITY_TYPE_PROMPT",
                children: [{
                    edge: "EDGE_TYPE_PROMPT_HAS_SIGNAL_TAGS",
                    mode: "JOIN_MODE_INNER",
                    target: { alias: "st", entity: "ENTITY_TYPE_SIGNAL_TAGS" },
                }],
            },
            timeRange: days(7),
            filter: { pred: {
                field: st("DIMENSION_FIELD_SIGNAL_TAGS_NAMESPACE"),
                op: "OPERATOR_LIKE",
                value: "security.%",
            } },
            output: {
                dimensions: [{ field: p("DIMENSION_FIELD_PROMPT_ENDPOINT_ID"), outputAlias: "endpoint" }],
                metrics: [
                    {
                        outputAlias: "signal_events",
                        computed: {
                            field: st("DIMENSION_FIELD_SIGNAL_TAGS_EVENT_ID"),
                            op: "AGGREGATE_OP_COUNT_DISTINCT",
                        },
                    },
                    {
                        outputAlias: "last_seen",
                        computed: {
                            field: p("DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP"),
                            op: "AGGREGATE_OP_MAX",
                        },
                    },
                ],
            },
            orderBy: [
                ...desc("signal_events"),
                { outputAlias: "endpoint", direction: "DIRECTION_ASC" },
            ],
            limit: 10,
        },
    }],
    render: {
        kind: "bars",
        labelField: "endpoint",
        valueField: "signal_events",
        format: "integer",
        color: "var(--orange)",
        link: { dim: "endpoints", idField: "endpoint" },
        unresolvedText: "Endpoint name unavailable",
        maxItems: 10,
        empty: "No endpoint Signals in the last seven days",
    },
});

const SIGNAL_PROVIDER_LABELS = `labels = {
    "anthropic": "Claude / Anthropic",
    "openai": "ChatGPT, Codex / OpenAI",
    "google": "Gemini / Google",
    "microsoft": "Copilot / Microsoft",
    "cursor": "Cursor",
    "xai": "xAI",
    "deepseek": "DeepSeek",
    "local": "Local AI",
}
`;

const signalRateByProvider = k({
    id: BUILTIN_KERNEL_IDS["signal-rate-by-provider"],
    command: "signal-rate-by-provider",
    title: "Signal rate by AI provider",
    description: "The share of each AI provider's traces that carried a Signal in the last seven days.",
    steps: [
        {
            kind: "analytics_query", id: "totals", spec: {
                root: PROMPT_ROOT,
                timeRange: days(7),
                filter: { pred: {
                    field: p("DIMENSION_FIELD_PROMPT_PROVIDER"),
                    op: "OPERATOR_NE",
                    value: "",
                } },
                output: {
                    dimensions: [{ field: p("DIMENSION_FIELD_PROMPT_PROVIDER"), outputAlias: "provider" }],
                    metrics: [distinct("total_traces", "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID")],
                },
                orderBy: [{ outputAlias: "provider", direction: "DIRECTION_ASC" }],
                limit: 1000,
            },
        },
        {
            kind: "analytics_query", id: "signaled", spec: {
                root: {
                    alias: "p",
                    entity: "ENTITY_TYPE_PROMPT",
                    children: [{
                        edge: "EDGE_TYPE_PROMPT_HAS_SIGNAL_TAGS",
                        mode: "JOIN_MODE_INNER",
                        target: { alias: "st", entity: "ENTITY_TYPE_SIGNAL_TAGS" },
                    }],
                },
                timeRange: days(7),
                filter: { andExpr: { children: [
                    { pred: {
                        field: p("DIMENSION_FIELD_PROMPT_PROVIDER"),
                        op: "OPERATOR_NE",
                        value: "",
                    } },
                    { pred: {
                        field: st("DIMENSION_FIELD_SIGNAL_TAGS_NAMESPACE"),
                        op: "OPERATOR_LIKE",
                        value: "security.%",
                    } },
                ] } },
                output: {
                    dimensions: [{ field: p("DIMENSION_FIELD_PROMPT_PROVIDER"), outputAlias: "provider" }],
                    metrics: [
                        distinct("signal_traces", "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID"),
                        distinct("endpoints", "DIMENSION_FIELD_PROMPT_ENDPOINT_ID"),
                    ],
                },
                orderBy: [{ outputAlias: "provider", direction: "DIRECTION_ASC" }],
                limit: 1000,
            },
        },
        {
            kind: "python",
            id: "rows",
            inputs: { totals: "totals", signaled: "signaled" },
            code: `${SIGNAL_PROVIDER_LABELS}totals = {
    str(row.get("provider") or ""): int(row.get("total_traces") or 0)
    for row in (inputs.get("totals") or [])
    if row.get("provider")
}
findings = {
    str(row.get("provider") or ""): row
    for row in (inputs.get("signaled") or [])
    if row.get("provider")
}
result = []
for provider, total_traces in totals.items():
    finding = findings.get(provider) or {}
    signal_traces = int(finding.get("signal_traces") or 0)
    endpoints = int(finding.get("endpoints") or 0)
    rate = (signal_traces * 1000 / total_traces) if total_traces else 0
    endpoint_noun = "endpoint" if endpoints == 1 else "endpoints"
    result.append({
        "provider": provider,
        "provider_label": labels.get(provider.lower(), provider.replace("_", " ").replace("-", " ").title()),
        "signal_rate": rate,
        "rate_label": f"{rate:,.1f} / 1K",
        "context": f"{signal_traces:,} of {total_traces:,} traces | {endpoints:,} {endpoint_noun}",
    })
result.sort(key=lambda row: (-row["signal_rate"], row["provider_label"]))
result = result[:8]
`,
        },
    ],
    renderSource: "rows",
    render: {
        kind: "rows",
        primaryField: "provider_label",
        secondaryField: "context",
        rightField: "rate_label",
        link: { dim: "providers", idField: "provider", labelField: "provider_label" },
        maxRows: 8,
        empty: "No AI provider activity in the last seven days",
    },
});

const workClusterRoot = (withSignals = false) => ({
    alias: "p",
    entity: "ENTITY_TYPE_PROMPT",
    children: [
        {
            edge: "EDGE_TYPE_PROMPT_HAS_EVENT_CLUSTER",
            mode: "JOIN_MODE_INNER",
            target: {
                alias: "ec",
                entity: "ENTITY_TYPE_ILO_EVENT_CLUSTER",
                stepFilter: [{ pred: {
                    field: ec("DIMENSION_FIELD_ILO_EVENT_CLUSTER_CLUSTER_ID"),
                    op: "OPERATOR_GT",
                    value: 0,
                } }],
                children: [{
                    edge: "EDGE_TYPE_EVENT_CLUSTER_IN_CLUSTER",
                    mode: "JOIN_MODE_LEFT",
                    target: { alias: "c", entity: "ENTITY_TYPE_ILO_CLUSTER" },
                }],
            },
        },
        ...(withSignals ? [{
            edge: "EDGE_TYPE_PROMPT_HAS_SIGNAL_TAGS",
            mode: "JOIN_MODE_INNER",
            target: { alias: "st", entity: "ENTITY_TYPE_SIGNAL_TAGS" },
        }] : []),
    ],
});

const workClusterDimensions = () => [
    { field: ec("DIMENSION_FIELD_ILO_EVENT_CLUSTER_CLUSTER_ID"), outputAlias: "cluster_id" },
    { field: cluster("DIMENSION_FIELD_ILO_CLUSTER_CLUSTER_NAME"), outputAlias: "name" },
];

const SECURITY_SIGNAL_FILTER = { pred: {
    field: st("DIMENSION_FIELD_SIGNAL_TAGS_NAMESPACE"),
    op: "OPERATOR_LIKE",
    value: "security.%",
} };

const signalBearingWork = k({
    id: BUILTIN_KERNEL_IDS["signal-bearing-work"],
    command: "signal-bearing-work",
    title: "AI work associated with Signals",
    description: "Work categories with the most Signal-bearing traces in the last seven days.",
    steps: [
        {
            kind: "analytics_query", id: "signaled", spec: {
                root: workClusterRoot(true),
                timeRange: days(7),
                filter: SECURITY_SIGNAL_FILTER,
                output: {
                    dimensions: workClusterDimensions(),
                    metrics: [
                        distinct("signal_traces", "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID"),
                        distinct("endpoints", "DIMENSION_FIELD_PROMPT_ENDPOINT_ID"),
                    ],
                },
                orderBy: [
                    ...desc("signal_traces"),
                    { outputAlias: "cluster_id", direction: "DIRECTION_ASC" },
                ],
                limit: 100,
            },
        },
        {
            kind: "analytics_query", id: "mix", spec: {
                root: workClusterRoot(true),
                timeRange: days(7),
                filter: SECURITY_SIGNAL_FILTER,
                output: {
                    dimensions: [
                        ...workClusterDimensions(),
                        { field: st("DIMENSION_FIELD_SIGNAL_TAGS_NAMESPACE"), outputAlias: "signal" },
                    ],
                    metrics: [distinct("signal_traces", "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID")],
                },
                orderBy: [
                    ...desc("signal_traces"),
                    { outputAlias: "cluster_id", direction: "DIRECTION_ASC" },
                    { outputAlias: "signal", direction: "DIRECTION_ASC" },
                ],
                limit: 500,
            },
        },
        {
            kind: "python",
            id: "rows",
            inputs: { signaled: "signaled", mix: "mix" },
            code: `signal_labels = {
    "credscan": "Cred scan",
    "package_install": "Package install",
    "reverse_shell": "Reverse shell",
    "unicode_smuggling": "Unicode smuggling",
    "encoded_payload": "Encoded payload",
    "destructive_command": "Destructive command",
    "sensitive_path_access": "Sensitive path access",
}
top_signal = {}
for row in (inputs.get("mix") or []):
    cluster_id = str(row.get("cluster_id"))
    raw = str(row.get("signal") or "")
    key = raw.removeprefix("security.")
    label = signal_labels.get(key, key.replace("_", " ").capitalize())
    count = int(row.get("signal_traces") or 0)
    current = top_signal.get(cluster_id)
    if current is None or count > current[0] or (count == current[0] and label < current[1]):
        top_signal[cluster_id] = (count, label)
result = []
for row in (inputs.get("signaled") or []):
    item = dict(row)
    cluster_id = str(item.get("cluster_id"))
    signal_traces = int(item.get("signal_traces") or 0)
    endpoints = int(item.get("endpoints") or 0)
    raw_name = str(item.get("name") or "")
    item["name"] = raw_name.replace("_", " ").replace("-", " ").title() or "Unlabeled work category"
    item["signal_traces"] = signal_traces
    endpoint_noun = "endpoint" if endpoints == 1 else "endpoints"
    signal_label = (top_signal.get(cluster_id) or (0, "Signal mix unavailable"))[1]
    item["context"] = f"{endpoints:,} {endpoint_noun} | {signal_label} most common"
    result.append(item)
result.sort(key=lambda row: (-row["signal_traces"], row["name"]))
result = result[:6]
`,
        },
    ],
    renderSource: "rows",
    render: {
        kind: "rows",
        primaryField: "name",
        secondaryField: "context",
        rightField: "signal_traces",
        rightFormat: "integer",
        link: { dim: "clusters", idField: "cluster_id", labelField: "name" },
        maxRows: 6,
        empty: "No Signal-bearing work categories in the last seven days",
    },
});

const CONTROL_GAP_LABELS = `rows = inputs.get("rows") or []
labels = {
    "managed_settings_shadowed": "Managed AI policy was overridden",
    "otel_keys_overwritten": "Security telemetry settings were changed",
    "managed_settings_written_by_non_origin": "Managed AI settings changed outside the approved path",
}
result = [dict(row, finding=labels.get(row.get("finding"), str(row.get("finding") or "Unknown finding").replace("_", " ").title()), signal="config-shadow") for row in rows]
`;

const visibilityControlGaps = k({
    id: BUILTIN_KERNEL_IDS["visibility-control-gaps"],
    command: "visibility-control-gaps",
    title: "Policy enforcement gaps",
    description: "Endpoints where managed AI settings or security telemetry changed in the last 30 days. The current policy is not yet available in this view.",
    scopeMode: "global",
    steps: [
        {
            kind: "analytics_query", id: "q", spec: {
                root: { alias: "st", entity: "ENTITY_TYPE_SIGNAL_TAGS" },
                timeRange: days(30),
                filter: { andExpr: { children: [
                    { pred: { field: st("DIMENSION_FIELD_SIGNAL_TAGS_NAMESPACE"), op: "OPERATOR_EQ", value: "security.config_shadow" } },
                    { pred: { field: st("DIMENSION_FIELD_SIGNAL_TAGS_SUBJECT_TYPE"), op: "OPERATOR_EQ", value: "endpoint" } },
                ] } },
                output: {
                    dimensions: [{ field: st("DIMENSION_FIELD_SIGNAL_TAGS_TAG"), outputAlias: "finding" }],
                    metrics: [
                        {
                            outputAlias: "endpoints",
                            computed: {
                                field: st("DIMENSION_FIELD_SIGNAL_TAGS_SUBJECT_ID"),
                                op: "AGGREGATE_OP_COUNT_DISTINCT",
                            },
                        },
                        {
                            outputAlias: "last_seen",
                            computed: {
                                field: st("DIMENSION_FIELD_SIGNAL_TAGS_EVENT_TIME"),
                                op: "AGGREGATE_OP_MAX",
                            },
                        },
                    ],
                },
                orderBy: [
                    ...desc("endpoints"),
                    { outputAlias: "finding", direction: "DIRECTION_ASC" },
                ],
                limit: 10,
            },
        },
        { kind: "python", id: "labels", inputs: { rows: "q" }, code: CONTROL_GAP_LABELS },
    ],
    renderSource: "labels",
    render: {
        kind: "table",
        columns: [
            { id: "finding", label: "Policy gap", link: { dim: "signals", idField: "signal", labelField: "finding" } },
            { id: "endpoints", label: "Endpoints", format: "integer", align: "right" },
            { id: "last_seen", label: "Last detected", format: "timestamp", align: "right" },
        ],
        defaultSort: { id: "endpoints", dir: "desc" },
        maxRows: 10,
        empty: "No visibility configuration findings in range",
    },
});

// The shipped set, normalized + sorted by command — mirrors triggerStore's
// BUILTINS so the array + entries keep a stable identity across reads.
export const BUILTIN_KERNELS = [
    activitySummary,
    promptVolume,
    agentSurfaces,
    providerMix,
    modelMix,
    topEndpoints,
    toolLeaders,
    resourceLeaders,
    providerFootprint,
    localAiSummary,
    localAiEvidence,
    localAiDetail,
    localAiRuntimeExec,
    newModels,
    newProviders,
    newAgentSurfaces,
    tokenSummary,
    tokenByModel,
    tokenBySurface,
    securityActivitySummary,
    securityVolumeSummary,
    installedAgentFootprint,
    agentUseConcentration,
    observedWorkClusters,
    mcpServerPrevalence,
    skillPrevalence,
    memoryPrevalence,
    loudestSignals,
    endpointsWithSignals,
    signalRateByProvider,
    signalBearingWork,
    visibilityControlGaps,
].sort((a, b) => a.command.localeCompare(b.command));

export const BUILTIN_KERNEL_COMMANDS = new Set(BUILTIN_KERNELS.map((k) => k.command));
